<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import Avatar from 'next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();
const { currentAccount } = useAccount();

const selectedIds = ref(new Set());

const agentList = useMapGetter('agents/getAgents');
const uiFlags = useMapGetter('agents/getUIFlags');
const currentUserId = useMapGetter('getCurrentUserID');

const agentSeatLimit = computed(
  () => currentAccount.value?.limits?.agents?.allowed
);

const selectedCount = computed(() => selectedIds.value.size);

const canSubmit = computed(() => {
  return (
    selectedCount.value === agentSeatLimit.value &&
    selectedIds.value.has(currentUserId.value)
  );
});

const membershipTime = agent => {
  if (agent.account_user_created_at) {
    return new Date(agent.account_user_created_at).getTime();
  }
  return agent.id;
};

const sortNewestFirst = (left, right) => {
  const timeDiff = membershipTime(right) - membershipTime(left);
  if (timeDiff !== 0) return timeDiff;
  return right.id - left.id;
};

const initialSelectedIds = () => {
  const allowed = agentSeatLimit.value;
  const currentId = currentUserId.value;
  const agents = agentList.value;
  let selected = agents
    .filter(agent => !agent.seat_locked)
    .map(agent => agent.id);

  if (!selected.includes(currentId)) {
    selected.push(currentId);
  }

  if (selected.length > allowed) {
    const droppable = agents
      .filter(agent => selected.includes(agent.id) && agent.id !== currentId)
      .sort(sortNewestFirst);
    const dropIds = new Set(
      droppable.slice(0, selected.length - allowed).map(agent => agent.id)
    );
    selected = selected.filter(id => !dropIds.has(id));
  }

  if (selected.length < allowed) {
    const fill = agents
      .filter(agent => !selected.includes(agent.id))
      .sort((left, right) => sortNewestFirst(right, left));
    selected = selected.concat(
      fill.slice(0, allowed - selected.length).map(agent => agent.id)
    );
  }

  return selected;
};

const isCurrentUser = agent => agent.id === currentUserId.value;

const isSelected = agent => selectedIds.value.has(agent.id);

const setAgentSelected = (agent, checked) => {
  if (isCurrentUser(agent)) return;

  const next = new Set(selectedIds.value);
  if (checked) {
    next.add(agent.id);
  } else {
    next.delete(agent.id);
  }
  selectedIds.value = next;
};

const submitSeats = async () => {
  if (!canSubmit.value) return;

  try {
    await store.dispatch('agents/assignSeats', Array.from(selectedIds.value));
    useAlert(t('AGENT_MGMT.MANAGE_SEATS.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    const apiError = error?.response?.data?.error;
    useAlert(apiError || t('AGENT_MGMT.MANAGE_SEATS.API.ERROR_MESSAGE'));
  }
};

onMounted(() => {
  selectedIds.value = new Set(initialSelectedIds());
});
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('AGENT_MGMT.MANAGE_SEATS.TITLE')"
      :header-content="
        $t('AGENT_MGMT.MANAGE_SEATS.DESC', { n: agentSeatLimit })
      "
    />
    <form class="flex flex-col items-start w-full" @submit.prevent="submitSeats">
      <p class="mb-4 text-sm leading-5 text-n-slate-11">
        {{
          $t('AGENT_MGMT.MANAGE_SEATS.SELECTED', {
            selected: selectedCount,
            n: agentSeatLimit,
          })
        }}
      </p>
      <div class="flex flex-col w-full gap-2.5 mb-4">
        <label
          v-for="agent in agentList"
          :key="agent.id"
          class="flex items-center gap-3"
          :class="{ 'cursor-pointer': !isCurrentUser(agent) }"
        >
          <Checkbox
            :model-value="isSelected(agent)"
            :disabled="isCurrentUser(agent)"
            @update:model-value="checked => setAgentSelected(agent, checked)"
          />
          <Avatar
            :src="agent.thumbnail"
            :name="agent.name"
            :size="32"
            hide-offline-status
          />
          <div class="flex flex-col min-w-0">
            <span
              class="flex items-center gap-1.5 text-heading-3 text-n-slate-12"
            >
              <span class="truncate">{{ agent.name }}</span>
              <span
                v-if="isCurrentUser(agent)"
                class="shrink-0 font-normal text-body-main text-n-slate-11"
              >
                {{ $t('AGENT_MGMT.MANAGE_SEATS.YOU') }}
              </span>
              <span
                v-if="agent.seat_locked"
                v-tooltip.top="$t('AGENT_MGMT.LIST.SEAT_LOCKED')"
                class="inline-flex shrink-0 items-center text-n-amber-11"
              >
                <Icon icon="i-lucide-lock-keyhole" class="size-3.5" />
              </span>
            </span>
            <span class="text-body-main text-n-slate-11 truncate">
              {{ agent.email }}
            </span>
          </div>
        </label>
      </div>
      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="button"
          :label="$t('AGENT_MGMT.MANAGE_SEATS.CANCEL')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('AGENT_MGMT.MANAGE_SEATS.SUBMIT')"
          :disabled="!canSubmit || uiFlags.isUpdating"
          :is-loading="uiFlags.isUpdating"
        />
      </div>
    </form>
  </div>
</template>
